import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Heart, HeartPulse, Sparkles } from "lucide-react";
import { api } from "@/context/AuthContext";

export const MoodRevival = ({ t, user, setUser }) => {
  const m = t.mood;
  const mood = user.avatar_mood?.mood || "happy";
  const [progress, setProgress] = useState(0);
  const [revived, setRevived] = useState(null); // {dialogue, img}
  const [busy, setBusy] = useState(false);
  const lastPos = useRef(null);
  const doneRef = useRef(false);

  if (mood === "happy" && !revived) return null;

  const dying = mood === "dying";

  const onStroke = (x, y) => {
    if (doneRef.current || revived) return;
    if (lastPos.current) {
      const dist = Math.hypot(x - lastPos.current.x, y - lastPos.current.y);
      if (dist > 2) {
        setProgress((p) => {
          const next = Math.min(100, p + dist / 22);
          if (next >= 100 && !doneRef.current) {
            doneRef.current = true;
            revive();
          }
          return next;
        });
      }
    }
    lastPos.current = { x, y };
  };

  const revive = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/avatar/revive", { caresses: 100 });
      setRevived(data);
      setUser({ ...user, avatar_mood: { mood: "happy", img: data.img } });
      toast.success(m.revivedToast);
    } catch {
      toast.error("Error");
      doneRef.current = false;
      setProgress(0);
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative rounded-3xl border p-6 mb-6 overflow-hidden text-center ${
        revived
          ? "border-[#00FFAA]/50 bg-[#00FFAA]/5 shadow-[0_0_35px_rgba(0,255,170,0.2)]"
          : dying
            ? "border-[#FF4444]/40 bg-[#FF4444]/5 shadow-[0_0_30px_rgba(255,68,68,0.15)]"
            : "border-[#6b7bd6]/40 bg-[#6b7bd6]/5"
      }`}
      data-testid="mood-revival-card"
    >
      <AnimatePresence mode="wait">
        {revived ? (
          <motion.div key="revived" initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring" }}>
            <motion.img
              src={revived.img}
              alt="happy avatar"
              animate={{ y: [0, -12, 0], rotate: [0, 4, -4, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              className="w-36 h-36 object-contain mx-auto mb-4 drop-shadow-[0_0_30px_rgba(0,255,170,0.55)]"
              data-testid="revived-img"
            />
            <p className="text-sm text-white/90 leading-relaxed bg-[#00FFAA]/8 border border-[#00FFAA]/30 rounded-2xl px-5 py-4 max-w-md mx-auto" data-testid="revival-dialogue">
              {revived.dialogue}
            </p>
            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#00FFAA] mt-4 flex items-center justify-center gap-2">
              <Sparkles size={12} /> {m.revivedLabel}
            </p>
          </motion.div>
        ) : (
          <motion.div key="needy" exit={{ opacity: 0, scale: 0.9 }}>
            <p className={`text-[10px] font-black uppercase tracking-[0.25em] mb-3 flex items-center justify-center gap-2 ${dying ? "text-[#FF6B6B] animate-pulse" : "text-[#8b9bf6]"}`}>
              {dying ? <HeartPulse size={13} /> : <Heart size={13} />}
              {dying ? m.dyingTitle : m.sadTitle}
            </p>
            <motion.img
              src={user.avatar_mood.img}
              alt={mood}
              animate={dying ? { opacity: [0.55, 0.85, 0.55] } : { y: [0, -5, 0] }}
              transition={{ duration: dying ? 2.4 : 3, repeat: Infinity, ease: "easeInOut" }}
              onMouseMove={(e) => onStroke(e.clientX, e.clientY)}
              onTouchMove={(e) => { const tch = e.touches[0]; if (tch) onStroke(tch.clientX, tch.clientY); }}
              className="w-40 h-40 object-contain mx-auto mb-3 cursor-pointer select-none touch-none"
              style={{ filter: dying ? "drop-shadow(0 0 18px rgba(255,68,68,0.35))" : "drop-shadow(0 0 18px rgba(107,123,214,0.4))" }}
              draggable={false}
              data-testid="mood-avatar-img"
            />
            <p className="text-xs text-white/60 max-w-sm mx-auto mb-4 leading-relaxed" data-testid="mood-message">
              {dying ? m.dyingMsg : m.sadMsg}
            </p>
            {/* Caress meter */}
            <div className="max-w-xs mx-auto">
              <div className="h-3 rounded-full bg-white/5 border border-white/10 overflow-hidden mb-2">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#FF6EC7] to-[#00FFAA] transition-all duration-100 shadow-[0_0_12px_rgba(255,110,199,0.6)]"
                  style={{ width: `${progress}%` }}
                  data-testid="caress-meter"
                />
              </div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF6EC7] animate-pulse" data-testid="caress-hint">
                {busy ? "..." : m.caressHint} {Math.round(progress)}%
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
